import { defineStore } from 'pinia';
import { Config } from '@/config';
import { Constants } from '@/constants';
import apiService from '@/services/api.service';
import { useI18n } from 'vue-i18n';

import type { TimelineStepDto } from '@/services/types/api.types';

function getTimelineStepStatus(): any[] {
  const { t } = useI18n();
  return [
    {
      value: Constants.ProjectModule.TimelineStep.Status.Upcoming,
      label: t('project-module.timeline.status.upcoming'),
    },
    { value: Constants.ProjectModule.TimelineStep.Status.Current, label: t('project-module.timeline.status.current') },
    {
      value: Constants.ProjectModule.TimelineStep.Status.Completed,
      label: t('project-module.timeline.status.completed'),
    },
  ];
}

export interface TimelineStepState {
  timelineSteps: TimelineStepDto[];
  itemLoadingStates: Set<number>;
}

export const useTimelineStepsStore = defineStore({
  id: 'timelineSteps',
  state: (): TimelineStepState => ({
    timelineSteps: [],
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (projectModuleId: number) => state.itemLoadingStates.has(projectModuleId),
    getTimelineStepStatus: (state) => getTimelineStepStatus,
    orderedTimelineSteps: (state) => {
      return [...state.timelineSteps].sort((a, b) => a.order_num - b.order_num);
    },
  },
  actions: {
    async fetchTimelineStepsByModuleId(projectModuleId: number) {
      this.itemLoadingStates.add(projectModuleId);
      try {
        const { data } = await apiService.get<TimelineStepDto[]>(
          `${Config.api.v2}/project-modules/${projectModuleId}/timeline-steps`,
        );
        this.timelineSteps = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(projectModuleId);
      }
    },
    createTimelineStep(projectModuleId: number): TimelineStepDto {
      return {
        project_module_id: projectModuleId,
        title: { en: '', fr: '' },
        content: { en: '', fr: '' },
        status: Constants.ProjectModule.TimelineStep.Status.Upcoming,
        order_num: -1,
      };
    },
    async saveTimelineStep(step: TimelineStepDto) {
      try {
        if (step.id) {
          await apiService.put<TimelineStepDto>(`${Config.api.v2}/timeline-steps/${step.id}`, step);
        } else {
          await apiService.post<TimelineStepDto>(`${Config.api.v2}/timeline-steps`, step);
        }
        await this.fetchTimelineStepsByModuleId(step.project_module_id);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteTimelineStep(step: TimelineStepDto) {
      try {
        await apiService.delete(`${Config.api.v2}/timeline-steps/${step.id!}`);
        await this.fetchTimelineStepsByModuleId(step.project_module_id);
      } catch (error) {
        console.error(error);
      }
    },
    async updateTimelineSteps(...steps: TimelineStepDto[]) {
      try {
        for (const step of steps) {
          await apiService.put(`${Config.api.v2}/timeline-steps/${step.id}`, step);
        }
      } catch (error) {
        console.error(error);
      }
    },
  },
});
